import {
  buildPath,
  buildTemplatePath,
  CrudMode,
  Page,
  PageRoute,
  pn,
  Stack,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import {
  CustomDataModelField,
  CustomDataModelFieldViewResource,
  LkCustomDataModelFieldTypeId,
  Permission,
} from "generated/openApi";
import produce from "immer";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import ApiService from "services/apiService";
import { BasicInfo } from "./BasicInfo";
import { CustomDataModelFieldPageHeader } from "./CustomDataModelFieldPageHeader";
import { SelectListItems } from "./SelectListItems/SelectListItems";

const routeName = "customDataModelField";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

export const customDataModelFieldPageRoute: PageRoute<Params> = {
  handle: "Custom Field",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const CustomDataModelFieldPage = () => {
  const ROOT_PERMISSION: Permission = Permission.SystemUtility;
  const { params } = useParams(routeName, Params);
  const navigate = useNavigate();
  const [customDataModelField, setCustomDataModelField] = useState<CustomDataModelField>({} as CustomDataModelField);
  const [viewResource, setViewResource] = useState<CustomDataModelFieldViewResource>({});
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [customDataModelField] });
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource();
  }, []);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${customDataModelFieldPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.customDataModelFields.get(id).then((response) => {
      setCustomDataModelField(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = () => {
    ApiService.customDataModelFields.getViewResource().then((response) => {
      setViewResource(response);
    });
  };

  return (
    <Page
      isForm
      header={
        <CustomDataModelFieldPageHeader
          rootPermission={ROOT_PERMISSION}
          crudMode={params.crudMode}
          reroute={reroute}
          fetch={fetch}
          handle={customDataModelFieldPageRoute.handle}
          customDataModelField={customDataModelField}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
        />
      }
    >
      <Stack spacing="lg">
        <BasicInfo
          customDataModelField={customDataModelField}
          setCustomDataModelField={setCustomDataModelField}
          viewResource={viewResource}
          crudMode={params.crudMode}
          validationErrors={validationErrors}
        />

        {params.id > 0 && customDataModelField?.fieldTypeId === Number(LkCustomDataModelFieldTypeId.SelectList) && (
          <SelectListItems
            rootPermission={ROOT_PERMISSION}
            id={params.id}
            selectList={customDataModelField.selectList || []}
            setSelectList={(value) => {
              setCustomDataModelField(
                produce(customDataModelField, (draft) => {
                  draft.selectList = value;
                })
              );
            }}
            validationErrors={validationErrors[pn<CustomDataModelField>("selectList")] || []}
          />
        )}
      </Stack>
    </Page>
  );
};
